import React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import AppShell from "../../components/AppShell";
import {
  fetchLatestSensorValues,
  fetchMonitorDeviceOverview,
  monitorLogout,
  sendMonitorControl,
} from "../../api/client";
import {
  evaluateDeviceAlerts,
  loadAcknowledgedAlerts,
} from "../alerts/alert-utils";
import { resolveSkinComponent } from "../../skins";
import "../shared/MonitorFlowPage.css";

function buildLatestByItem(latest) {
  const rows = latest?.items || [];
  return rows.reduce((acc, row) => {
    if (row?.item?.id) {
      acc[row.item.id] = row.event || null;
    }
    return acc;
  }, {});
}

export default function DeviceMonitorPage() {
  const navigate = useNavigate();
  const { deviceKey } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "";
  const [overview, setOverview] = React.useState(null);
  const [latestByItem, setLatestByItem] = React.useState({});
  const [lastRefreshAt, setLastRefreshAt] = React.useState("");
  const [commandStatus, setCommandStatus] = React.useState({});
  const [error, setError] = React.useState("");
  const [acknowledgedAlerts] = React.useState(() => loadAcknowledgedAlerts());

  React.useEffect(() => {
    let mounted = true;

    fetchMonitorDeviceOverview(deviceKey, category)
      .then((data) => {
        if (!mounted) return;
        setOverview(data);
      })
      .catch((loadError) => {
        if (!mounted) return;
        setError(loadError.message || "디바이스 정보를 불러오지 못했습니다.");
      });

    return () => {
      mounted = false;
    };
  }, [category, deviceKey]);

  React.useEffect(() => {
    let mounted = true;

    const loadLatest = () => {
      fetchLatestSensorValues(deviceKey)
        .then((data) => {
          if (!mounted) return;
          setLatestByItem(buildLatestByItem(data));
          setLastRefreshAt(new Date().toLocaleTimeString());
        })
        .catch(() => {
          if (!mounted) return;
          setLatestByItem({});
        });
    };

    loadLatest();
    const timer = window.setInterval(loadLatest, 5000);

    return () => {
      mounted = false;
      window.clearInterval(timer);
    };
  }, [deviceKey]);

  const handleLogout = async () => {
    await monitorLogout();
    navigate("/login", { replace: true });
  };

  const handleCategoryClick = (name) => {
    if (!name) {
      setSearchParams({});
      return;
    }
    setSearchParams({ category: name });
  };

  const SelectedItemSkin = resolveSkinComponent(overview?.device?.frontend_skin);
  const computedAlerts = React.useMemo(() => {
    if (!overview?.items?.length) {
      return [];
    }
    return evaluateDeviceAlerts({
      deviceKey,
      items: overview.items,
      latestByItem,
      acknowledgedMap: acknowledgedAlerts,
    });
  }, [acknowledgedAlerts, deviceKey, latestByItem, overview]);

  const handleControl = async (item, controlIndex, buttonIndex) => {
    const key = `${item.id}:${controlIndex}:${buttonIndex}`;
    setCommandStatus((prev) => ({ ...prev, [key]: "전송 중" }));
    try {
      await sendMonitorControl(deviceKey, item.id, controlIndex, buttonIndex);
      setCommandStatus((prev) => ({ ...prev, [key]: "명령 전송 완료" }));
    } catch (sendError) {
      setCommandStatus((prev) => ({
        ...prev,
        [key]: sendError.message || "명령 전송 실패",
      }));
    }
  };

  return (
    <AppShell
      title={overview?.device?.name || "디바이스"}
      subtitle="디바이스에 등록된 분류와 항목 목록입니다."
      actions={<button className="action-btn" onClick={handleLogout} title="로그아웃">
        <svg viewBox="-8 -5 40 30" preserveAspectRatio="none" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M2 6.5C2 4.01472 4.01472 2 6.5 2H12C14.2091 2 16 3.79086 16 6V7C16 7.55228 15.5523 8 15 8C14.4477 8 14 7.55228 14 7V6C14 4.89543 13.1046 4 12 4H6.5C5.11929 4 4 5.11929 4 6.5V17.5C4 18.8807 5.11929 20 6.5 20H12C13.1046 20 14 19.1046 14 18V17C14 16.4477 14.4477 16 15 16C15.5523 16 16 16.4477 16 17V18C16 20.2091 14.2091 22 12 22H6.5C4.01472 22 2 19.9853 2 17.5V6.5ZM18.2929 8.29289C18.6834 7.90237 19.3166 7.90237 19.7071 8.29289L22.7071 11.2929C23.0976 11.6834 23.0976 12.3166 22.7071 12.7071L19.7071 15.7071C19.3166 16.0976 18.6834 16.0976 18.2929 15.7071C17.9024 15.3166 17.9024 14.6834 18.2929 14.2929L19.5858 13L11 13C10.4477 13 10 12.5523 10 12C10 11.4477 10.4477 11 11 11L19.5858 11L18.2929 9.70711C17.9024 9.31658 17.9024 8.68342 18.2929 8.29289Z" fill="#0F1729"/>
        </svg>
      </button>}
    >
      <div className="dv-flow-grid">
        {error ? <div className="error-box">{error}</div> : null}
        {overview ? (
          <>
            <section className="device__card">
              <div className="dv_card_body">
                <div className="dv-summary-table">
                  <strong><span>디바이스 정보</span></strong>
                  <div>
                    <span className="label">디바이스 키</span>
                    <strong>{overview.device.device_key}</strong>
                  </div>
                  <div>
                    <span className="label">상태</span>
                    <strong>{overview.device.status || "-"}</strong>
                  </div>
                  <div>
                    <span className="label">그룹</span>
                    <strong>{overview.device.group?.name || "-"}</strong>
                  </div>
                  <div>
                    <span className="label">프론트 스킨</span>
                    <strong>{overview.device.frontend_skin || "monitor_card"}</strong>
                  </div>
                  <div>
                    <span className="label">최근 갱신</span>
                    <strong>{lastRefreshAt ? lastRefreshAt : null}</strong>
                  </div>
                </div>
              </div>
            </section>

            <section className="option-card">
              <div className="opt-header">
                
                <div className="filter_tabs">
                  <button type="button" className={!category ? "active" : ""} onClick={() => handleCategoryClick("")}>
                    분류 전체
                  </button>
                  {(overview.categories || []).map((item) => (
                    <button
                      key={item.name}
                      type="button"
                      className={item.selected ? "active" : ""}
                      onClick={() => handleCategoryClick(item.name)}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
                
              </div>
              <div className="opt-body">
                {!overview.items.length ? (
                  <div className="empty-state">선택한 분류에 등록된 항목이 없습니다.</div>
                ) : (
                  <div className="item-list item-list--skin">
                    {overview.items.map((item) => (
                      <article key={item.id} className="item-card item-card--skin">
                        <div className="item-card__link">
                          <SelectedItemSkin
                            overview={overview}
                            item={item}
                            latestEvent={latestByItem[item.id] || null}
                            latestPayload={latestByItem[item.id]?.payload || null}
                            latestReceivedAt={latestByItem[item.id]?.received_at || null}
                            deviceName={item.name}
                            displayId={item.device_number}
                            status={item.status_label || item.status}
                            controls={item.control_profile || []}
                            rules={item.sensor_rules || []}
                            alerts={computedAlerts.filter((alert) => alert.item_id === item.id)}
                            events={[]}
                            onControl={handleControl}
                            commandStatus={
                              Object.entries(commandStatus)
                                .filter(([key]) => key.startsWith(`${item.id}:`))
                                .map(([, value]) => value)
                                .slice(-1)[0] || ""
                            }
                          />
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </>
        ) : (
          <div className="empty-state">디바이스 정보를 불러오는 중입니다.</div>
        )}
      </div>
    </AppShell>
  );
}
